import React, { useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Check, X, ArrowRight, Star, ShieldCheck, Eye, BadgeCheck, MessageSquare,
  Search, Award, LayoutList, Inbox, ClipboardCheck,
} from "lucide-react";
import Seo from "@/components/Seo";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function track(event_type, metadata = {}) {
  try {
    fetch(`${BACKEND}/api/analytics/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event_type, metadata, source_page: "/pricing" }),
      keepalive: true,
    }).catch(() => {});
  } catch (_) { /* noop */ }
}

const VERIFIED_STRIPE = "https://buy.stripe.com/28E4gycGzai20Ld7S08og00";
const FEATURED_STRIPE = "https://buy.stripe.com/6oUcN4eOH3TEfG73BK8og01";

const TIERS = [
  {
    key: "basic",
    name: "Basic",
    price: "Free",
    priceSuffix: "",
    trial: null,
    badge: null,
    icon: ShieldCheck,
    bestFor: "Companies that want to be included in the directory and visible to buyers at a basic level.",
    features: [
      "Company profile",
      "Website link",
      "Basic category placement",
      "Basic region and industry tags",
      "Claim listing option",
      "Admin-reviewed updates",
    ],
    cta: "Claim or submit listing",
    ctaLink: "/list-your-company",
    ctaEvent: "pricing_basic_cta_clicked",
    footnote: null,
    border: "border-slate-200",
  },
  {
    key: "verified",
    name: "Verified",
    price: "$197",
    priceSuffix: "/month",
    trial: "14-day free trial",
    badge: "Most popular",
    icon: BadgeCheck,
    highlight: true,
    bestFor: "Providers that want credibility, stronger profile content and a clearer route to buyer enquiries.",
    features: [
      "Verified badge",
      "Enhanced company profile",
      "Stronger buyer trust signals",
      "More complete technical positioning",
      "Quote / enquiry routing when enabled",
      "Provider dashboard access when enabled",
      "Up to 3 profile images",
      "Admin-reviewed profile improvements",
    ],
    cta: "Start Verified trial",
    ctaLink: VERIFIED_STRIPE,
    ctaEvent: "pricing_verified_cta_clicked",
    footnote: "All Verified listings are manually reviewed before activation.",
    border: "border-[#002FA7]",
  },
  {
    key: "featured",
    name: "Featured",
    price: "$297",
    priceSuffix: "/month",
    trial: "14-day free trial",
    badge: "Best visibility",
    icon: Star,
    bestFor: "Providers that want higher visibility across relevant searches, categories and buyer journeys.",
    features: [
      "Featured badge",
      "Priority visibility where relevant",
      "Featured placement on selected category or use-case pages",
      "Enhanced profile layout",
      "Up to 8 images",
      "Video embed",
      "Brochure or case study links",
      "Stronger calls to action",
      "Monthly performance summary",
      "Includes all Verified benefits",
    ],
    cta: "Start Featured trial",
    ctaLink: FEATURED_STRIPE,
    ctaEvent: "pricing_featured_cta_clicked",
    footnote: "Featured placement is manually approved by Strategic Tracking.",
    border: "border-amber-300",
  },
];

const BENEFITS = [
  {
    icon: Eye,
    title: "Buyer visibility",
    body: "Appear in front of buyers researching tracking solutions by use case, industry, region, technology and sensor capability.",
  },
  {
    icon: BadgeCheck,
    title: "Credibility",
    body: "Verified listings help buyers distinguish active, reviewed providers from basic unclaimed company profiles.",
  },
  {
    icon: MessageSquare,
    title: "Lead generation",
    body: "Enhanced listings create a clearer route for buyers to request information, compare providers and start conversations.",
  },
];

const ROI = [
  { icon: Search,       title: "Discovery",   body: "Be found by buyers searching for tracking solutions by industry, region, technology and use case." },
  { icon: Award,        title: "Credibility", body: "Use your verified directory profile as a third-party reference point in sales conversations." },
  { icon: LayoutList,   title: "Positioning", body: "Present your capabilities clearly across devices, software, sensors, connectivity, compliance and regions served." },
  { icon: Inbox,        title: "Enquiries",   body: "Give buyers a structured way to request information or quotes from your company." },
];

const AUDIENCE = [
  "Tracking device manufacturers",
  "Cold chain monitoring providers",
  "IoT sensor and smart label companies",
  "Asset tracking platforms",
  "Shipment visibility providers",
  "BLE, GPS, cellular, LoRaWAN and satellite tracking providers",
  "System integrators and tracking solution partners",
];

const COMPARE_ROWS = [
  ["Public company profile", true, true, true],
  ["Website link", true, true, true],
  ["Basic category tags", true, true, true],
  ["Claim listing", true, true, true],
  ["Verified badge", false, true, true],
  ["Enhanced profile content", false, true, true],
  ["Buyer enquiry CTA", false, true, true],
  ["Provider dashboard", false, true, true],
  ["Image gallery", false, "Up to 3", "Up to 8"],
  ["Video embed", false, false, true],
  ["Case study / brochure links", false, false, true],
  ["Featured badge", false, false, true],
  ["Priority placement", false, false, true],
  ["Category visibility", "Basic", "Standard", "Priority"],
  ["Monthly performance summary", false, false, true],
  ["Admin-assisted listing support", false, true, true],
];

const FAQS = [
  {
    q: "Is the Basic listing really free?",
    a: "Yes. Basic listings are free and allow your company to be included in the directory. Enhanced visibility and lead-generation features are available through Verified and Featured plans.",
  },
  {
    q: "What is included in the 14-day free trial?",
    a: "The trial lets suitable providers experience the Verified or Featured listing option before continuing on a monthly plan. Activation remains subject to Strategic Tracking review.",
  },
  {
    q: "Do paid listings guarantee leads?",
    a: "No specific lead volume is guaranteed. Verified and Featured listings are designed to improve visibility, credibility and buyer enquiry opportunities as the directory grows.",
  },
  {
    q: "Can Strategic Tracking build my listing for me?",
    a: "Yes. Strategic Tracking can manually build or improve listings for providers, especially for Verified and Featured profiles.",
  },
  {
    q: "Can I cancel?",
    a: "Yes. Monthly plans should be cancellable, subject to the payment and listing terms shown during signup.",
  },
  {
    q: "Why are listings manually reviewed?",
    a: "Manual review protects directory quality and ensures that enhanced listings are relevant, accurate and appropriate for buyer-facing promotion.",
  },
];

function CellIcon({ v }) {
  if (v === true)  return <Check size={16} className="text-emerald-500 inline"/>;
  if (v === false) return <X size={16} className="text-slate-300 inline"/>;
  return <span className="text-xs font-semibold text-slate-700">{v}</span>;
}

function TierCTA({ tier }) {
  const cls = `mt-6 text-sm font-semibold inline-flex items-center justify-center gap-2 rounded-md h-11 transition-colors w-full ${
    tier.highlight
      ? "bg-[#002FA7] text-white hover:bg-[#00227A]"
      : tier.key === "featured"
        ? "bg-slate-900 text-white hover:bg-slate-800"
        : "border border-slate-200 text-slate-900 hover:bg-slate-50"
  }`;
  const testid = `pricing-cta-${tier.key}`;
  if (tier.ctaLink.startsWith("http")) {
    return (
      <a href={tier.ctaLink} target="_blank" rel="noopener noreferrer"
        onClick={() => track(tier.ctaEvent, { tier: tier.name, destination: "stripe" })}
        className={cls} data-testid={testid}>
        {tier.cta} <ArrowRight size={14}/>
      </a>
    );
  }
  return (
    <Link to={tier.ctaLink} onClick={() => track(tier.ctaEvent, { tier: tier.name })}
      className={cls} data-testid={testid}>
      {tier.cta} <ArrowRight size={14}/>
    </Link>
  );
}

function Faq({ item }) {
  const onToggle = (e) => {
    if (e.target.open) track("pricing_faq_opened", { question: item.q });
  };
  return (
    <details onToggle={onToggle}
      className="border-b border-slate-200 py-4 group cursor-pointer"
      data-testid={`pricing-faq-${item.q.slice(0, 24).toLowerCase().replace(/[^a-z]+/g, '-')}`}>
      <summary className="flex items-center justify-between font-display font-semibold text-slate-900 list-none">
        <span>{item.q}</span>
        <span className="text-[#002FA7] group-open:rotate-45 transition-transform text-xl leading-none select-none">+</span>
      </summary>
      <p className="mt-3 text-sm text-slate-600 leading-relaxed">{item.a}</p>
    </details>
  );
}

export default function Pricing() {
  useEffect(() => { track("pricing_page_viewed"); }, []);

  const trackClaim = useCallback(() => track("pricing_claim_clicked"), []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="pricing-page">
      <Seo
        title="Provider Listing Tiers & Pricing"
        description="List your tracking solution on Strategic Tracking. Three tiers — Basic (free), Verified ($197/mo) and Featured ($297/mo). Both paid tiers include a 14-day free trial. Built to help tracking, IoT and visibility providers get discovered by buyers."
      />

      {/* HERO */}
      <section className="max-w-4xl mx-auto text-center">
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Provider tiers</div>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-slate-900 mt-3 tracking-tight leading-[1.05]"
          data-testid="pricing-hero-h1">
          Get discovered by buyers looking for tracking solutions.
        </h1>
        <p className="mt-6 text-lg text-slate-600 leading-relaxed">
          Strategic Tracking helps logistics, cold chain, pharma, supply chain and asset tracking
          buyers find and compare tracking device, IoT, visibility and monitoring providers globally.
        </p>
        <p className="mt-4 text-base text-slate-500">
          Basic listings are free. Verified and Featured listings help providers improve visibility,
          credibility and buyer enquiry opportunities.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <a href={VERIFIED_STRIPE} target="_blank" rel="noopener noreferrer"
            onClick={() => track("pricing_verified_cta_clicked", { location: "hero" })}
            data-testid="pricing-hero-trial"
            className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-[#002FA7] text-white font-semibold text-sm hover:bg-[#00227A] transition-colors">
            Start 14-day trial <ArrowRight size={14}/>
          </a>
          <Link to="/list-your-company"
            onClick={trackClaim}
            data-testid="pricing-hero-claim"
            className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md border border-slate-300 text-slate-900 font-semibold text-sm hover:bg-slate-50 transition-colors">
            Claim free listing
          </Link>
        </div>
      </section>

      {/* VALUE PROP */}
      <section className="mt-24" data-testid="pricing-benefits">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Why upgrade</div>
          <h2 className="font-display font-bold text-3xl text-slate-900 mt-2">Why upgrade your listing?</h2>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {BENEFITS.map((b) => {
            const Icon = b.icon;
            return (
              <div key={b.title} className="card-elevated p-6" data-testid={`pricing-benefit-${b.title.toLowerCase().replace(/\s+/g,'-')}`}>
                <div className="w-11 h-11 rounded-md bg-[#002FA7]/10 text-[#002FA7] grid place-items-center">
                  <Icon size={20}/>
                </div>
                <div className="mt-5 font-display font-bold text-lg text-slate-900">{b.title}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{b.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* PRICING CARDS */}
      <section className="mt-24" data-testid="pricing-tiers">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Choose your tier</div>
          <h2 className="font-display font-bold text-3xl text-slate-900 mt-2">Three plans. One directory.</h2>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-6xl mx-auto">
          {TIERS.map((t) => {
            const Icon = t.icon;
            return (
              <div key={t.key}
                className={`relative card-elevated p-7 flex flex-col border-2 ${t.border} ${t.highlight ? "shadow-lg md:scale-[1.02]" : ""}`}
                data-testid={`pricing-tier-${t.key}`}>
                {t.badge && (
                  <span className={`absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] font-bold uppercase tracking-widest px-3 py-1 rounded-full ${
                    t.key === "verified" ? "bg-[#002FA7] text-white" : "bg-amber-400 text-slate-900"
                  }`}>
                    {t.badge}
                  </span>
                )}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md bg-slate-900 text-white grid place-items-center"><Icon size={18}/></div>
                  <div className="font-display font-extrabold text-2xl text-slate-900">{t.name}</div>
                </div>

                <div className="mt-5 flex items-baseline gap-1">
                  <span className="text-4xl font-display font-extrabold text-slate-900">{t.price}</span>
                  {t.priceSuffix && <span className="text-sm text-slate-500 font-semibold">{t.priceSuffix}</span>}
                </div>
                {t.trial && (
                  <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-emerald-600">
                    {t.trial}
                  </div>
                )}

                <p className="mt-4 text-sm text-slate-600 leading-relaxed">
                  <span className="font-semibold text-slate-800">Best for: </span>{t.bestFor}
                </p>

                <ul className="mt-6 space-y-2.5 text-sm flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check size={16} className="text-emerald-500 mt-0.5 shrink-0"/>
                      <span className="text-slate-800">{f}</span>
                    </li>
                  ))}
                </ul>

                <TierCTA tier={t}/>

                {t.footnote && (
                  <p className="mt-3 text-xs text-slate-500 text-center leading-snug">{t.footnote}</p>
                )}
              </div>
            );
          })}
        </div>
        <p className="mt-8 text-center text-sm text-slate-500" data-testid="pricing-manual-activation-note">
          All listings are reviewed and activated manually by Strategic Tracking.
        </p>
      </section>

      {/* COMPARISON TABLE */}
      <section className="mt-24" data-testid="pricing-compare">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Feature comparison</div>
          <h2 className="font-display font-bold text-3xl text-slate-900 mt-2">Compare Basic, Verified and Featured</h2>
        </div>
        <div className="mt-10 overflow-x-auto card-elevated">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <th className="py-4 px-5 font-display font-semibold text-slate-500 uppercase tracking-wide text-xs">Feature</th>
                <th className="py-4 px-5 font-display font-bold text-slate-900">Basic</th>
                <th className="py-4 px-5 font-display font-bold text-[#002FA7]">Verified</th>
                <th className="py-4 px-5 font-display font-bold text-slate-900">Featured</th>
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map(([label, b, v, f]) => (
                <tr key={label} className="border-b border-slate-100 last:border-0">
                  <td className="py-3 px-5 text-slate-700">{label}</td>
                  <td className="py-3 px-5"><CellIcon v={b}/></td>
                  <td className="py-3 px-5 bg-[#002FA7]/5"><CellIcon v={v}/></td>
                  <td className="py-3 px-5"><CellIcon v={f}/></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* TRUST / MANUAL REVIEW */}
      <section className="mt-24 max-w-4xl mx-auto card-elevated p-10 bg-slate-50" data-testid="pricing-manual-review">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-md bg-slate-900 text-white grid place-items-center shrink-0">
            <ClipboardCheck size={20}/>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Manual review</div>
            <h2 className="font-display font-bold text-2xl text-slate-900 mt-1">Reviewed for quality, not automatically activated.</h2>
            <p className="mt-4 text-slate-700 leading-relaxed">
              Strategic Tracking manually reviews listing claims, verification requests and featured
              placements. This protects the quality of the directory and helps buyers trust that
              enhanced listings have been reviewed before being promoted.
            </p>
            <p className="mt-3 text-slate-700 leading-relaxed">
              Payment or trial request does not automatically change a listing. Strategic Tracking
              confirms activation, profile quality and appropriate placement manually.
            </p>
          </div>
        </div>
      </section>

      {/* ROI */}
      <section className="mt-24" data-testid="pricing-roi">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Return on listing</div>
          <h2 className="font-display font-bold text-3xl text-slate-900 mt-2">How to think about ROI</h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            The value of an enhanced listing is not only direct enquiries. It also supports buyer
            discovery, category positioning, sales credibility and visibility when prospects are
            researching tracking providers.
          </p>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {ROI.map((r) => {
            const Icon = r.icon;
            return (
              <div key={r.title} className="card-elevated p-6" data-testid={`pricing-roi-${r.title.toLowerCase()}`}>
                <div className="w-11 h-11 rounded-md bg-[#002FA7]/10 text-[#002FA7] grid place-items-center">
                  <Icon size={20}/>
                </div>
                <div className="mt-5 font-display font-bold text-lg text-slate-900">{r.title}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{r.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* AUDIENCE */}
      <section className="mt-24 max-w-4xl mx-auto" data-testid="pricing-audience">
        <div className="text-center">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Who this is for</div>
          <h2 className="font-display font-bold text-3xl text-slate-900 mt-2">Who should upgrade?</h2>
        </div>
        <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-3">
          {AUDIENCE.map((line) => (
            <li key={line} className="flex items-start gap-3 card-elevated p-4">
              <Check size={16} className="text-emerald-500 mt-1 shrink-0"/>
              <span className="text-slate-800">{line}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* FAQ */}
      <section className="mt-24 max-w-3xl mx-auto" data-testid="pricing-faq">
        <div className="text-center">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Frequently asked</div>
          <h2 className="font-display font-bold text-3xl text-slate-900 mt-2">Pricing questions</h2>
        </div>
        <div className="mt-8">
          {FAQS.map((f) => <Faq key={f.q} item={f}/>)}
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="mt-24" data-testid="pricing-bottom-cta">
        <div className="card-elevated p-10 md:p-14 bg-slate-900 text-white text-center rounded-xl">
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">
            Ready to improve your visibility in the tracking solution market?
          </h2>
          <p className="mt-4 text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Claim your free listing or start a 14-day trial for enhanced visibility.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/list-your-company"
              onClick={() => { trackClaim(); track("pricing_claim_clicked", { location: "bottom" }); }}
              data-testid="pricing-bottom-claim"
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-white text-slate-900 font-semibold text-sm hover:bg-slate-100 transition-colors">
              Claim free listing
            </Link>
            <a href={VERIFIED_STRIPE} target="_blank" rel="noopener noreferrer"
              onClick={() => track("pricing_verified_cta_clicked", { location: "bottom" })}
              data-testid="pricing-bottom-verified"
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-[#002FA7] text-white font-semibold text-sm hover:bg-[#00227A] transition-colors">
              Start Verified trial <ArrowRight size={14}/>
            </a>
            <a href={FEATURED_STRIPE} target="_blank" rel="noopener noreferrer"
              onClick={() => track("pricing_featured_cta_clicked", { location: "bottom" })}
              data-testid="pricing-bottom-featured"
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-amber-400 text-slate-900 font-semibold text-sm hover:bg-amber-300 transition-colors">
              Start Featured trial <ArrowRight size={14}/>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
